import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputText from "../../../components/forms/InputText.jsx";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

function AppointmentsDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const locationType = location.state?.type || "";
	const data = useMemo(() => location.state?.data || [], [location.state?.data]);
	const selectedDate = location.state?.selectedDate;

	const [appointment, setAppointment] = useState({
		nombreCliente: "",
		telefonoCliente: "",
		emailCliente: "",
		fechaHoraCita: "",
		nombreVehiculo: "",
		marcaVehiculo: "",
		modeloVehiculo: "",
		anoVehiculo: "",
		nombreVendedor: "",
		idSucursal: "",
		estado: "Confirmada",
	});
	const { handleDelete, handleSubmit } = useHandleActionItem();

	// Función para convertir datos traducidos a campos originales
	const translateDataToOriginal = (translatedData) => {
		const fieldMapping = {
			"ID Cita": "idCita",
			"Nombre Cliente": "nombreCliente",
			"Teléfono Cliente": "telefonoCliente",
			"Email Cliente": "emailCliente",
			"Fecha Hora Cita": "fechaHoraCita",
			"Nombre Vehículo": "nombreVehiculo",
			"Marca Vehículo": "marcaVehiculo",
			"Modelo Vehículo": "modeloVehiculo",
			"Año Vehículo": "anoVehiculo",
			"Nombre Vendedor": "nombreVendedor",
			"ID Sucursal": "idSucursal",
			"Estado": "estado",
			"Fecha Creación": "fechaCreacion",
		};

		const originalData = {};
		for (const [translatedKey, value] of Object.entries(translatedData)) {
			const originalKey = fieldMapping[translatedKey] || translatedKey;
			originalData[originalKey] = value;
		}
		return originalData;
	};

	useEffect(() => {
		if (locationType === "edit" && data) {
			const originalData = translateDataToOriginal(data);
			// Convertir fecha para input datetime-local
			if (originalData.fechaHoraCita) {
				const date = new Date(originalData.fechaHoraCita);
				const year = date.getFullYear();
				const month = (date.getMonth() + 1).toString().padStart(2, "0");
				const day = date.getDate().toString().padStart(2, "0");
				const hours = date.getHours().toString().padStart(2, "0");
				const minutes = date.getMinutes().toString().padStart(2, "0");
				originalData.fechaHoraCita = `${year}-${month}-${day}T${hours}:${minutes}`;
			}
			setAppointment(originalData);
		} else if (locationType === "add" && selectedDate) {
			// Si se seleccionó una fecha en el calendario, usarla como fecha por defecto
			const date = new Date(selectedDate);
			const year = date.getFullYear();
			const month = (date.getMonth() + 1).toString().padStart(2, "0");
			const day = date.getDate().toString().padStart(2, "0");
			const defaultTime = "09:00";
			const defaultDateTime = `${year}-${month}-${day}T${defaultTime}`;
			setAppointment((prev) => ({ ...prev, fechaHoraCita: defaultDateTime }));
		}
	}, [locationType, data, selectedDate]);

	const validateForm = () => {
		if (!appointment.nombreCliente || !appointment.telefonoCliente || !appointment.fechaHoraCita) {
			Toast.fire({
				icon: "error",
				title: "Nombre, teléfono y fecha son obligatorios",
				background: "#1e2836",
			});
			return false;
		}
		return true;
	};

	const handleInputChange = (field, value) => {
		setAppointment((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;
		
		try {
			// Convertir fecha de input datetime-local a formato ISO para base de datos
			const appointmentData = { ...appointment };
			if (appointmentData.fechaHoraCita) {
				appointmentData.fechaHoraCita = new Date(appointmentData.fechaHoraCita).toISOString().slice(0, 19).replace('T', ' ');
			}
			
			if (locationType === "add") {
				await handleSubmit("add", "appointments", appointmentData);
			} else {
				await handleSubmit("update", "appointments", appointmentData);
			}
			navigate("/appointments");
		} catch (error) {
			console.log("ERROR", error);
		}
	};

	const handleDeleteForm = async (event) => {
		event.preventDefault();
		await handleDelete({ idCita: appointment.idCita }, "appointments", true);
	};

	const estadoOptions = [
		{ value: "Confirmada", label: "Confirmada" },
		{ value: "Cancelada", label: "Cancelada" },
		{ value: "Realizada", label: "Realizada" },
	];

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{locationType === "add" ? "Nueva Cita" : "Actualizar Cita"}</h1>
				
				<div className="row">
					<div className="col-md-6">
						<h4>Información del Cliente</h4>
						<InputText
							label="Nombre del Cliente"
							value={appointment.nombreCliente || ""}
							setValue={(v) => handleInputChange("nombreCliente", v)}
						/>
						<InputText
							label="Teléfono del Cliente"
							value={appointment.telefonoCliente || ""}
							setValue={(v) => handleInputChange("telefonoCliente", v)}
						/>
						<InputText
							label="Email del Cliente"
							value={appointment.emailCliente || ""}
							setValue={(v) => handleInputChange("emailCliente", v)}
						/>
					</div>
					
					<div className="col-md-6">
						<h4>Información del Vehículo</h4>
						<InputText
							label="Nombre del Vehículo"
							value={appointment.nombreVehiculo || ""}
							setValue={(v) => handleInputChange("nombreVehiculo", v)}
						/>
						<InputText
							label="Marca del Vehículo"
							value={appointment.marcaVehiculo || ""}
							setValue={(v) => handleInputChange("marcaVehiculo", v)}
						/>
						<InputText
							label="Modelo del Vehículo"
							value={appointment.modeloVehiculo || ""}
							setValue={(v) => handleInputChange("modeloVehiculo", v)}
						/>
						<InputText
							label="Año del Vehículo"
							value={appointment.anoVehiculo || ""}
							setValue={(v) => handleInputChange("anoVehiculo", v)}
						/>
					</div>
				</div>

				<div className="row">
					<div className="col-md-6">
						<h4>Detalles de la Cita</h4>
						<div className="mb-3">
							<label className="form-label">Fecha y Hora de la Cita</label>
							<input
								type="datetime-local"
								className="form-control"
								value={appointment.fechaHoraCita || ""}
								onChange={(e) => handleInputChange("fechaHoraCita", e.target.value)}
							/>
						</div>
						<InputText
							label="Nombre del Vendedor"
							value={appointment.nombreVendedor || ""}
							setValue={(v) => handleInputChange("nombreVendedor", v)}
						/>
						<InputText
							label="ID Sucursal"
							value={appointment.idSucursal || ""}
							setValue={(v) => handleInputChange("idSucursal", v)}
						/>
					</div>
					
					<div className="col-md-6">
						<h4>Estado</h4>
						<div className="mb-3">
							<label className="form-label">Estado de la Cita</label>
							<select
								className="form-control"
								value={appointment.estado || "Confirmada"}
								onChange={(e) => handleInputChange("estado", e.target.value)}>
								{estadoOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				<hr />
				<div className="mb-3 d-flex justify-content-around">
					<button type="submit" className="btn btn-outline-theme btn-lg">
						{locationType === "add" ? "Crear Cita" : "Actualizar Cita"}
					</button>
					<button
						type="button"
						onClick={() => navigate("/appointments")}
						className="btn btn-outline-theme btn-lg">
						Atrás
					</button>
					{locationType === "edit" && (
						<button
							type="button"
							onClick={handleDeleteForm}
							className="btn btn-outline-danger btn-lg">
							Eliminar
						</button>
					)}
				</div>
			</form>
		</div>
	);
}

export default AppointmentsDetails;
